import { supabase } from '@/lib/supabase';
import type { OrgMessage } from '@/types';

export const messagesApi = {
  getWhatsappAccounts: async (
    orgId: string
  ): Promise<{ id: string; name: string; phone_number: string | null }[]> => {
    const { data, error } = await supabase
      .from('org_whatsapp_accounts')
      .select('id, name, phone_number')
      .eq('organization_id', orgId)
      .order('name');

    if (error) throw error;
    return (data ?? []) as { id: string; name: string; phone_number: string | null }[];
  },

  listForOrg: async (
    orgId: string,
    opts: { limit?: number; contactId?: string; channel?: string } = {}
  ): Promise<OrgMessage[]> => {
    let q = supabase
      .from('org_messages')
      .select('*, whatsapp_account:org_whatsapp_accounts(id,name,phone_number)')
      .eq('organization_id', orgId)
      .order('sent_at', { ascending: false })
      .limit(opts.limit ?? 100);

    if (opts.contactId) q = q.eq('contact_id', opts.contactId);
    if (opts.channel)   q = q.eq('channel', opts.channel);

    const { data, error } = await q;
    if (error) throw error;
    return data as OrgMessage[];
  },

  listForContact: async (
    contactId: string,
    opts: { limit?: number } = {}
  ): Promise<OrgMessage[]> => {
    const { data, error } = await supabase
      .from('org_messages')
      .select('*, whatsapp_account:org_whatsapp_accounts(id,name,phone_number)')
      .eq('contact_id', contactId)
      .order('sent_at', { ascending: false })
      .limit(opts.limit ?? 100);

    if (error) throw error;
    return data as OrgMessage[];
  },

  send: async (params: {
    org_id: string;
    whatsapp_account_id: string;
    to_address: string;
    content: string;
    channel: 'whatsapp' | 'sms';
    contact_id?: string;
  }): Promise<OrgMessage> => {
    const { data: account, error: accErr } = await supabase
      .from('org_whatsapp_accounts')
      .select('instance_id, api_token, phone_number')
      .eq('id', params.whatsapp_account_id)
      .single();

    if (accErr || !account) throw new Error('חשבון WhatsApp לא נמצא');

    const phone = params.to_address.replace(/\D/g, '');
    const chatId = phone.startsWith('972') ? `${phone}@c.us` : `972${phone.replace(/^0/, '')}@c.us`;

    const res = await fetch(
      `https://api.green-api.com/waInstance${account.instance_id}/sendMessage/${account.api_token}`,
      {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ chatId, message: params.content }),
      }
    );
    if (!res.ok) {
      const err = await res.text();
      throw new Error(`שגיאת GreenAPI: ${err}`);
    }
    const result = await res.json();

    const { data, error } = await supabase
      .from('org_messages')
      .insert({
        organization_id: params.org_id,
        contact_id: params.contact_id ?? null,
        whatsapp_account_id: params.whatsapp_account_id,
        channel: params.channel,
        direction: 'outbound',
        from_address: account.phone_number,
        to_address: params.to_address,
        content: params.content,
        external_id: result.idMessage ?? null,
        status: 'sent',
        sent_at: new Date().toISOString(),
      })
      .select()
      .single();

    if (error) throw error;
    return data as OrgMessage;
  },
};
